import { useState } from "react";
import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, ArrowLeft, Mail, CheckCircle } from "lucide-react";

const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");
const LOGO = `${import.meta.env.BASE_URL}logo.png`;

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const [fallbackLink, setFallbackLink] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await fetch(`${BASE}/api/auth/forgot-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || "Something went wrong"); return; }
      if (data.resetLink) setFallbackLink(data.resetLink);
      setSent(true);
    } catch {
      setError("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-indigo-950 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="flex items-center justify-center gap-3 mb-8">
          <img src={LOGO} alt="RetailPulse" className="w-10 h-10 rounded-xl object-cover" />
          <span className="text-2xl font-bold text-white">RetailPulse</span>
        </div>

        <div className="bg-white rounded-2xl p-8 shadow-xl">
          {sent ? (
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto">
                <CheckCircle className="w-8 h-8 text-emerald-600" />
              </div>
              {fallbackLink ? (
                <>
                  <h2 className="text-xl font-bold text-gray-900">Reset link ready</h2>
                  <p className="text-gray-500 text-sm">
                    Email delivery isn't set up yet. Use the link below to reset your password — it expires in 1 hour.
                  </p>
                  <a
                    href={fallbackLink}
                    className="block w-full text-center bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-2.5 px-4 rounded-lg text-sm transition-colors"
                  >
                    Click here to reset password
                  </a>
                  <p className="text-xs text-gray-400 break-all">{fallbackLink}</p>
                </>
              ) : (
                <>
                  <h2 className="text-xl font-bold text-gray-900">Check your email</h2>
                  <p className="text-gray-500 text-sm">
                    We've sent a password reset link to <strong className="text-gray-700">{email}</strong>.
                    The link expires in 1 hour.
                  </p>
                  <p className="text-xs text-gray-400">
                    Didn't receive it? Check your spam folder or{" "}
                    <button
                      type="button"
                      onClick={() => setSent(false)}
                      className="text-indigo-600 hover:underline font-medium"
                    >
                      try again
                    </button>.
                  </p>
                </>
              )}
              <Link href="/login">
                <Button variant="outline" className="w-full mt-2 gap-2">
                  <ArrowLeft className="w-4 h-4" /> Back to Login
                </Button>
              </Link>
            </div>
          ) : (
            <>
              <div className="mb-6">
                <div className="w-12 h-12 bg-indigo-100 rounded-xl flex items-center justify-center mb-4">
                  <Mail className="w-6 h-6 text-indigo-600" />
                </div>
                <h2 className="text-xl font-bold text-gray-900">Forgot your password?</h2>
                <p className="text-gray-500 text-sm mt-1">
                  Enter your email and we'll send you a link to reset your password.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-gray-700">Email address</label>
                  <Input
                    type="email"
                    placeholder="you@yourstore.com"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    required
                    autoComplete="email"
                    className="mt-1"
                  />
                </div>

                {error && (
                  <p className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded-md">{error}</p>
                )}

                <Button type="submit" className="w-full gap-2" disabled={loading}>
                  {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                  Send Reset Link
                </Button>
              </form>

              <div className="mt-5 text-center">
                <Link href="/login" className="text-sm text-indigo-600 hover:underline flex items-center justify-center gap-1.5">
                  <ArrowLeft className="w-3.5 h-3.5" /> Back to Login
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
